import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { X, Save, Upload, Loader2 } from 'lucide-react';

interface ClientConfig {
  id: string;
  user_id: string;
  company_name: string;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  visible_columns: string[] | null;
}

interface SettingsProps {
  onClose: () => void;
}

export default function Settings({ onClose }: SettingsProps) {
  const { user } = useAuth();
  const [config, setConfig] = useState<ClientConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    loadConfig();
  }, [user]);

  const loadConfig = async () => {
    try {
      // Explicitly select only non-sensitive fields - api_key is never exposed to regular users
      const { data, error } = await supabase
        .from('clients')
        .select('id, user_id, company_name, logo_url, primary_color, secondary_color, visible_columns')
        .eq('user_id', user?.id)
        .maybeSingle();

      if (error) throw error;
      setConfig(data as ClientConfig);
    } catch (err) {
      alert('Erro ao carregar configurações: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('clients')
        .update({
          company_name: config.company_name,
          primary_color: config.primary_color,
          secondary_color: config.secondary_color,
          visible_columns: config.visible_columns
        })
        .eq('user_id', user?.id);

      if (error) throw error;
      alert('Configurações salvas com sucesso! Recarregue a página para ver as mudanças.');
      onClose();
    } catch (err) {
      alert('Erro ao salvar: ' + (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !config) return;

    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione uma imagem');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert('Imagem muito grande. Máximo 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const fileExt = file.name.split('.').pop();
      // Use random UUID for filename to prevent user ID enumeration
      const fileName = `${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('client-logos')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('client-logos')
        .getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from('clients')
        .update({ logo_url: publicUrl })
        .eq('user_id', user?.id);

      if (updateError) throw updateError;

      setConfig({ ...config, logo_url: publicUrl });
      alert('Logo atualizado com sucesso!');
    } catch (err) {
      alert('Erro ao fazer upload: ' + (err as Error).message);
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="bg-galaxy-card border-galaxy-border">
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!config) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="bg-galaxy-card border-galaxy-border">
          <div className="p-4 text-center text-muted-foreground">
            Configuração não encontrada
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'rgba(26, 11, 46, 0.95)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(124, 58, 237, 0.3)',
        borderRadius: '16px',
        padding: '24px',
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ color: '#E5E7EB', fontSize: '20px', fontWeight: '700', margin: 0 }}>
            Personalizar CRM
          </h2>
          <button
            onClick={onClose}
            style={{
              padding: '8px',
              background: 'rgba(124, 58, 237, 0.2)',
              border: '1px solid rgba(124, 58, 237, 0.3)',
              borderRadius: '8px',
              color: '#A78BFA',
              cursor: 'pointer'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Nome da Empresa */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', color: '#E5E7EB', fontSize: '14px', marginBottom: '8px' }}>
            Nome da Empresa
          </label>
          <input
            type="text"
            value={config.company_name}
            onChange={(e) => setConfig({ ...config, company_name: e.target.value })}
            style={{
              width: '100%',
              padding: '12px',
              background: 'rgba(10, 1, 24, 0.6)',
              border: '1px solid rgba(124, 58, 237, 0.3)',
              borderRadius: '8px',
              color: '#E5E7EB',
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>

        {/* Logo */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', color: '#E5E7EB', fontSize: '14px', marginBottom: '8px' }}>
            Logo da Empresa
          </label>
          {config.logo_url && (
            <div style={{ marginBottom: '12px', padding: '12px', background: 'rgba(10, 1, 24, 0.6)', borderRadius: '8px' }}>
              <img src={config.logo_url} alt="Logo" style={{ maxHeight: '60px', objectFit: 'contain' }} />
            </div>
          )}
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            background: 'rgba(124, 58, 237, 0.2)',
            border: '1px solid rgba(124, 58, 237, 0.3)',
            borderRadius: '8px',
            color: '#A78BFA',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}>
            <Upload size={18} />
            {uploadingLogo ? 'Enviando...' : 'Enviar Logo'}
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              style={{ display: 'none' }}
            />
          </label>
          <p style={{ color: '#6B7280', fontSize: '12px', marginTop: '8px' }}>
            Formato: PNG, JPG ou SVG. Tamanho máximo: 2MB
          </p>
        </div>

        {/* Cores */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', color: '#E5E7EB', fontSize: '14px', marginBottom: '8px' }}>
              Cor Primária
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="color"
                value={config.primary_color || '#7C3AED'}
                onChange={(e) => setConfig({ ...config, primary_color: e.target.value })}
                style={{
                  width: '50px',
                  height: '40px',
                  border: '1px solid rgba(124, 58, 237, 0.3)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: 'transparent'
                }}
              />
              <input
                type="text"
                value={config.primary_color || '#7C3AED'}
                onChange={(e) => setConfig({ ...config, primary_color: e.target.value })}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'rgba(10, 1, 24, 0.6)',
                  border: '1px solid rgba(124, 58, 237, 0.3)',
                  borderRadius: '8px',
                  color: '#E5E7EB',
                  fontSize: '14px',
                  fontFamily: 'monospace'
                }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', color: '#E5E7EB', fontSize: '14px', marginBottom: '8px' }}>
              Cor Secundária
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="color"
                value={config.secondary_color || '#6B46C1'}
                onChange={(e) => setConfig({ ...config, secondary_color: e.target.value })}
                style={{
                  width: '50px',
                  height: '40px',
                  border: '1px solid rgba(124, 58, 237, 0.3)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: 'transparent'
                }}
              />
              <input
                type="text"
                value={config.secondary_color || '#6B46C1'}
                onChange={(e) => setConfig({ ...config, secondary_color: e.target.value })}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'rgba(10, 1, 24, 0.6)',
                  border: '1px solid rgba(124, 58, 237, 0.3)',
                  borderRadius: '8px',
                  color: '#E5E7EB',
                  fontSize: '14px',
                  fontFamily: 'monospace'
                }}
              />
            </div>
          </div>
        </div>

        {/* Colunas Visíveis */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', color: '#E5E7EB', fontSize: '14px', marginBottom: '8px' }}>
            Colunas Visíveis no CRM
          </label>
          <p style={{ color: '#6B7280', fontSize: '12px', marginBottom: '12px' }}>
            Selecione quais campos serão exibidos nos cards
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {['nome_cliente', 'telefone', 'categoria_produto', 'item_pedido', 'endereco_entrega', 'prazo_entrega', 'temperatura_lead', 'status_compra'].map(col => (
              <label key={col} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={(config.visible_columns || []).includes(col)}
                  onChange={(e) => {
                    const cols = config.visible_columns || [];
                    if (e.target.checked) {
                      setConfig({ ...config, visible_columns: [...cols, col] });
                    } else {
                      setConfig({ ...config, visible_columns: cols.filter(c => c !== col) });
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ color: '#E5E7EB', fontSize: '13px' }}>
                  {col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Botões */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '12px',
              background: 'rgba(124, 58, 237, 0.2)',
              border: '1px solid rgba(124, 58, 237, 0.3)',
              borderRadius: '8px',
              color: '#A78BFA',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '12px',
              background: 'linear-gradient(135deg, #6B46C1 0%, #7C3AED 100%)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1
            }}
          >
            <Save size={16} />
            {saving ? 'Salvando...' : 'Salvar Alterações'}
          </button>
        </div>
      </div>
    </div>
  );
}
